import { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Scatter,
} from "recharts";

export default function App() {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  // ✅ report marker ref (draggable pin)
  const reportMarkerRef = useRef(null);

  // ✅ localStorage keys
  const LS_WIND_UNIT = "sporty.units.wind";
  const LS_WAVE_UNIT = "sporty.units.wave";

  // ✅ wave multiplier (persisted)
  const LS_WAVE_MULT = "sporty.wave.multiplier";
  const WAVE_MULTS = [1, 2];

  // ✅ allowed values (guards against bad/old saved values)
  const WIND_UNITS = ["kt", "kmh", "mph"];
  const WAVE_UNITS = ["m", "ft"];

  const [overlays, setOverlays] = useState([]);
  const [positions, setPositions] = useState({});
  const [activeOverlay, setActiveOverlay] = useState(null);

  // ✅ wave height multiplier (1 = reported, 2 = doubled)
  const [waveMultiplier, setWaveMultiplier] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_WAVE_MULT);
      const n = Number(raw);
      return WAVE_MULTS.includes(n) ? n : 2; // default to 2
    } catch {
      return 2;
    }
  });

  // ✅ units (persisted)
  const [windUnit, setWindUnit] = useState(() =>
    safeGetUnit(LS_WIND_UNIT, WIND_UNITS, "kt")
  );

  const [waveUnit, setWaveUnit] = useState(() =>
    safeGetUnit(LS_WAVE_UNIT, WAVE_UNITS, "m")
  );

  const WIND_DIR_MAP = {
    N: 0,
    NNE: 22.5,
    NE: 45,
    ENE: 67.5,
    E: 90,
    ESE: 112.5,
    SE: 135,
    SSE: 157.5,
    S: 180,
    SSW: 202.5,
    SW: 225,
    WSW: 247.5,
    W: 270,
    WNW: 292.5,
    NW: 315,
    NNW: 337.5,
  };

  // ✅ history modal state
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyError, setHistoryError] = useState("");
  const [historyTarget, setHistoryTarget] = useState(null);

  // ✅ tab state
  const [historyTab, setHistoryTab] = useState("graph"); // "graph" | "list"

  // ✅ Self Report modal state (SIMPLIFIED)
  const [reportOpen, setReportOpen] = useState(false);
  const [reportPicking, setReportPicking] = useState(false); // ✅ pick-on-map mode
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportSuccess, setReportSuccess] = useState("");
  const [recentReports, setRecentReports] = useState([]);
  const [reportPositions, setReportPositions] = useState({});
  const [activeReport, setActiveReport] = useState(null);

  // report pin is required (user clicks map to set it)
  const [reportPin, setReportPin] = useState(null); // { lng, lat } | null

  const [reportForm, setReportForm] = useState({
    waveHeight: "", // numeric string
    condition: "calm", // calm | choppy | rough
    description: "", // optional
  });

  const reportPickingRef = useRef(false);

  useEffect(() => {
    reportPickingRef.current = reportPicking;
  }, [reportPicking]);

  useEffect(() => {
    if (!WAVE_MULTS.includes(waveMultiplier)) return;
    try {
      localStorage.setItem(LS_WAVE_MULT, String(waveMultiplier));
    } catch {
      // ignore
    }
  }, [waveMultiplier]);

  useEffect(() => {
  let alive = true;

  const load = async () => {
    try {
      const base = process.env.REACT_APP_BASE_URL || "/";
      const res = await fetch(`${base}api/reports/recent?hours=4`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      const rows = Array.isArray(data?.rows) ? data.rows : [];
      const normalized = rows.map((r) => ({
        ...r,
        lat: Number(r.lat),
        lng: Number(r.lng),
        wave_height_m: Number(r.wave_height_m),
      }));

      if (alive) setRecentReports(normalized);
    } catch (e) {
      console.error("Failed to fetch recent reports:", e);
    }
  };

  load();
  const t = setInterval(load, 60 * 1000); // refresh every minute
  return () => {
    alive = false;
    clearInterval(t);
  };
}, []);

useEffect(() => {
  const map = mapRef.current;
  if (!map) return;

  const update = () => {
    const next = {};
    for (const r of recentReports) {
      if (!Number.isFinite(r.lng) || !Number.isFinite(r.lat)) continue;
      const p = map.project([r.lng, r.lat]);
      next[r.id] = { x: p.x, y: p.y };
    }
    setReportPositions(next);
  };

  map.on("load", update);
  map.on("render", update);
  update();

  return () => {
    map.off("load", update);
    map.off("render", update);
  };
}, [recentReports]);

  async function checkRecentWeatherReports() {
    const base = process.env.REACT_APP_BASE_URL || "/";

    const res = await fetch(`${base}api/reports/recent?hours=4`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    return res.json();
  }

  // ----------------------------
  // LocalStorage helpers
  // ----------------------------
  function safeGetUnit(key, allowed, fallback) {
    try {
      const v = localStorage.getItem(key);
      return allowed.includes(v) ? v : fallback;
    } catch {
      return fallback;
    }
  }

  function safeSetUnit(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  }

function setReportPinOnMap(lng, lat, { fly = true } = {}) {
  const map = mapRef.current;
  if (!map) return;

  const apply = () => {
    setReportPin({ lng, lat });

    if (!reportMarkerRef.current) {
      const marker = new mapboxgl.Marker({ draggable: true })
        .setLngLat([lng, lat])
        .addTo(map);

      marker.on("dragend", () => {
        const p = marker.getLngLat();
        setReportPin({ lng: p.lng, lat: p.lat });
      });

      reportMarkerRef.current = marker;
    } else {
      reportMarkerRef.current.setLngLat([lng, lat]);
    }

    if (fly) {
      map.flyTo({
        center: [lng, lat],
        zoom: Math.max(map.getZoom(), 10),
        speed: 1.2,
      });
    }
  };

  // ✅ wait for style load if needed
  if (map.loaded()) apply();
  else map.once("load", apply);
}

  useEffect(() => {
    if (!WIND_UNITS.includes(windUnit)) return;
    safeSetUnit(LS_WIND_UNIT, windUnit);
  }, [windUnit]);

  useEffect(() => {
    if (!WAVE_UNITS.includes(waveUnit)) return;
    safeSetUnit(LS_WAVE_UNIT, waveUnit);
  }, [waveUnit]);

  // ----------------------------
  // History modal open/close
  // ----------------------------
  function openHistory(overlay) {
    setHistoryTarget(overlay);
    setHistoryTab("graph");
    setHistoryOpen(true);
  }

  function closeHistory() {
    setHistoryOpen(false);
    setHistoryRows([]);
    setHistoryError("");
    setHistoryTarget(null);
    setHistoryTab("graph");
  }

  // ----------------------------
  // Self report open/close + pick mode
  // ----------------------------
function enterPickMode() {
  setReportError("");
  setReportSuccess("");
  setReportPicking(true);

  const map = mapRef.current;
  if (map) {
    map.getCanvas().style.cursor = "crosshair";
  } else {
    document.body.style.cursor = "crosshair";
  }
}

function exitPickMode() {
  setReportPicking(false);

  const map = mapRef.current;
  if (map) {
    map.getCanvas().style.cursor = "";
  }
  document.body.style.cursor = "";
}

  function removeReportMarker() {
    if (reportMarkerRef.current) {
      reportMarkerRef.current.remove();
      reportMarkerRef.current = null;
    }
  }

function openReport() {
  setReportError("");
  setReportSuccess("");
  setReportLoading(false);

  setReportForm({
    waveHeight: "",
    condition: "calm",
    description: "",
  });

  setReportOpen(true);

  // ✅ If a pin already exists, keep it and don't force pick mode
  if (reportPin) {
    exitPickMode();
    return;
  }

  // ✅ Try to default to user's current location
  if (!navigator.geolocation) {
    // no geolocation support -> fall back to pick mode
    enterPickMode();
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (pos) => {
      const lng = pos.coords.longitude;
      const lat = pos.coords.latitude;

      // set pin + marker at user location
      setReportPinOnMap(lng, lat, { fly: true });

      // ✅ don't force crosshair mode
      exitPickMode();
    },
   (err) => {
      console.warn("Geolocation failed:", err);

      setReportError(
        "Couldn’t get your current location (Position unavailable). You can pick a spot on the map, or turn on Location Services and try again."
      );

      // ✅ fallback: use current map center as default pin
      const map = mapRef.current;
      if (map) {
        const c = map.getCenter();
        setReportPinOnMap(c.lng, c.lat, { fly: false });
        exitPickMode(); // keep modal open with a default
      } else {
        enterPickMode();
      }
    },
    {
      enableHighAccuracy: true,
      timeout: 8000,
      maximumAge: 60_000,
    }
  );
}

  function closeReport() {
    setReportOpen(false);
    setReportLoading(false);
    setReportError("");
    setReportSuccess("");
    exitPickMode();
    // optional: keep pin for next time, or clear it:
    // setReportPin(null);
    // removeReportMarker();
  }

  // ----------------------------
  // Fetch overlay list
  // ----------------------------
  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        const base = process.env.REACT_APP_BASE_URL || "/";
        const res = await fetch(base + "api/overlays");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const normalized = (data || []).map((o) => ({
          ...o,
          lat: Number(o.lat),
          lng: Number(o.lng),
        }));

        if (alive) setOverlays(normalized);
      } catch (e) {
        console.error("Failed to fetch overlays:", e);
      }
    };

    load();
    const t = setInterval(load, 60 * 1000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  // ----------------------------
  // Init map once
  // ----------------------------
  useEffect(() => {
    if (mapRef.current) return;

    const token = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    if (!token) {
      console.error("Missing REACT_APP_MAPBOX_ACCESS_TOKEN in .env");
      return;
    }
    mapboxgl.accessToken = token;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/jcon12/cmkhqxs4h002101sr0k1fe3df",
      center: [-123.1207, 49.2827],
      zoom: 7.5,
    });

    mapRef.current = map;

    // click map clears overlay selection (unless in pick mode)
    map.on("click", () => {
      if (!reportPickingRef.current) {
        setActiveOverlay(null);
        setActiveReport(null);
      }
    });

    // ✅ pick pin when in pick mode
    map.on("click", (e) => {
      // ✅ read latest picking value from ref (not stale closure)
      if (!reportPickingRef.current) return;

      const { lng, lat } = e.lngLat;

      setReportPin({ lng, lat });

      if (!reportMarkerRef.current) {
        const marker = new mapboxgl.Marker({ draggable: true })
          .setLngLat([lng, lat])
          .addTo(map);

        marker.on("dragend", () => {
          const p = marker.getLngLat();
          setReportPin({ lng: p.lng, lat: p.lat });
        });

        reportMarkerRef.current = marker;
      } else {
        reportMarkerRef.current.setLngLat([lng, lat]);
      }

      exitPickMode();

      // ✅ reopen modal after the click finishes
      setTimeout(() => setReportOpen(true), 0);
    });

    return () => {
      removeReportMarker();
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------
  // Recompute projected positions
  // ----------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const update = () => {
      const next = {};
      for (const o of overlays) {
        if (!Number.isFinite(o.lng) || !Number.isFinite(o.lat)) continue;
        const p = map.project([o.lng, o.lat]);
        next[o.id] = { x: p.x, y: p.y };
      }
      setPositions(next);
    };

    map.on("load", update);
    map.on("render", update);
    update();

    return () => {
      map.off("load", update);
      map.off("render", update);
    };
  }, [overlays]);

  // ----------------------------
  // Helpers (wind + time)
  // ----------------------------
  function parseWindDirectionDegrees(windText) {
    if (!windText) return null;
    const s = String(windText).trim().toUpperCase();
    if (s.includes("CALM")) return null;

    const m = s.match(/^[A-Z]{1,3}/);
    const dirToken = m ? m[0] : null;
    return dirToken ? WIND_DIR_MAP[dirToken] ?? null : null;
  }

  function directionTokenOnly(windText) {
    if (!windText) return "—";
    const s = String(windText).trim().toUpperCase();
    if (s.includes("CALM")) return "CALM";
    const m = s.match(/^[A-Z]{1,3}/);
    return m ? m[0] : "—";
  }

  function parseWindSpeedKnots(windText) {
    if (windText == null) return null;
    const s = String(windText);
    if (s.toLowerCase().includes("calm")) return 0;
    const match = s.match(/\d+(\.\d+)?/);
    return match ? Number(match[0]) : null;
  }

  function convertWindFromKnots(kt, unit) {
    if (kt == null) return null;
    if (unit === "kt") return kt;
    if (unit === "kmh") return kt * 1.852;
    if (unit === "mph") return kt * 1.150779;
    return kt;
  }

  function convertWaveMeters(m, unit) {
    if (m == null) return null;
    if (unit === "m") return m;
    if (unit === "ft") return m * 3.28084;
    return m;
  }

  function convertWaveToMeters(value, unit) {
    if (value == null) return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    if (unit === "m") return n;
    if (unit === "ft") return n / 3.28084;
    return n;
  }

  function formatWindValueFromText(windText) {
    const kt = parseWindSpeedKnots(windText);
    const v = convertWindFromKnots(kt, windUnit);
    if (!Number.isFinite(v)) return "—";
    return `${Math.round(v)}`;
  }

  function formatWaveValue(meters) {
    const v = convertWaveMeters(Number(meters), waveUnit);
    if (!Number.isFinite(v)) return "—";
    const decimals = waveUnit === "m" ? 2 : 1;
    return `${v.toFixed(decimals)}`;
  }

  function pacificTimeOnly(zuluISOString) {
    if (!zuluISOString) return "—";
    return new Date(zuluISOString).toLocaleTimeString("en-CA", {
      timeZone: "America/Los_Angeles",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  function pacificDateTime(zuluISOString) {
    if (!zuluISOString) return "—";
    return new Date(zuluISOString).toLocaleString("en-CA", {
      timeZone: "America/Los_Angeles",
      hour12: false,
    });
  }

  function arrowFromDegrees(dirDeg) {
    if (typeof dirDeg !== "number") return "•";
    return "→";
  }

  // ----------------------------
  // Fetch history when modal opens
  // ----------------------------
  useEffect(() => {
    if (!historyOpen || !historyTarget) return;

    const controller = new AbortController();

    (async () => {
      try {
        setHistoryLoading(true);
        setHistoryError("");

        const base = process.env.REACT_APP_BASE_URL || "/";
        const stationId = historyTarget.station_id || historyTarget.id;

        const url =
          `${base}api/overlays/history` +
          `?station_id=${encodeURIComponent(stationId)}&hours=12`;

        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.rows)
          ? data.rows
          : [];

        rows.sort((a, b) => {
          const ta = new Date(a.observed_at || a.ts || a.timestamp || 0).getTime();
          const tb = new Date(b.observed_at || b.ts || b.timestamp || 0).getTime();
          return ta - tb;
        });

        setHistoryRows(rows);
      } catch (e) {
        if (e.name !== "AbortError") setHistoryError(String(e.message || e));
      } finally {
        setHistoryLoading(false);
      }
    })();

    return () => controller.abort();
  }, [historyOpen, historyTarget]);

  function useIsMobile(breakpointPx = 640) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= breakpointPx;
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpointPx);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpointPx]);

  return isMobile;
}

const isMobile = useIsMobile(640); // adjust breakpoint if you want

  // Modal metric is decided by the clicked icon (no user choice)
  const historyMetric = historyTarget?.type === "wave" ? "wave" : "wind";
  const historyMetricLabel =
    historyMetric === "wave" ? `Wave height (${waveUnit})` : `Wind speed (${windUnit})`;

  const chartData = useMemo(() => {
    return (historyRows || [])
      .map((r) => {
        const t = r.observed_at || r.ts || r.timestamp;
        const tsMs = t ? new Date(t).getTime() : null;

        const windKtRaw =
          r.wind_knots != null ? parseWindSpeedKnots(r.wind_knots) : null;
        const windSelected =
          windKtRaw == null ? null : convertWindFromKnots(windKtRaw, windUnit);

        const windDirDeg = parseWindDirectionDegrees(r.wind_knots);
        const windDirToken = directionTokenOnly(r.wind_knots);

        const waveMRaw =
          r.wave_height_m != null ? Number(r.wave_height_m) : null;
        const waveSelected =
          waveMRaw == null
            ? null
            : convertWaveMeters(waveMRaw * waveMultiplier, waveUnit);

        return {
          tsMs,
          time: t ? pacificTimeOnly(t) : "—",
          wind: Number.isFinite(windSelected) ? Number(windSelected) : null,
          wave: Number.isFinite(waveSelected) ? Number(waveSelected) : null,
          dirDeg: typeof windDirDeg === "number" ? windDirDeg : null,
          dir: windDirToken,
          raw: r,
        };
      })
      .filter((d) => Number.isFinite(d.tsMs))
      .sort((a, b) => a.tsMs - b.tsMs);
  }, [historyRows, windUnit, waveUnit]);

  const tooltipLabel = (label) => `Time: ${label}`;

  const tooltipFormatter = (value) => {
    const unit = historyMetric === "wave" ? waveUnit : windUnit;
    if (value == null || Number.isNaN(value)) return ["—", historyMetricLabel];
    const decimals = historyMetric === "wave" ? (waveUnit === "m" ? 2 : 1) : 1;
    return [`${Number(value).toFixed(decimals)} ${unit}`, historyMetricLabel];
  };

  const dirMarkers = useMemo(() => {
    if (historyMetric !== "wind") return [];
    return chartData
      .filter((d) => d.wind != null && d.dirDeg != null)
      .map((d) => ({
        time: d.time,
        wind: d.wind,
        dirDeg: d.dirDeg,
        dir: d.dir,
      }));
  }, [chartData, historyMetric]);

  useEffect(() => {
  let alive = true;

  (async () => {
    try {
      const data = await checkRecentWeatherReports();

      if (!alive) return;

      if (data) {
        console.log("⚠️", data);
      }
    } catch (e) {
      console.error("Recent report check failed:", e);
    }
  })();

  return () => {
    alive = false;
  };
}, []);


  // ----------------------------
  // Submit Self Report
  // ----------------------------
  async function submitReport() {
    try {
      setReportLoading(true);
      setReportError("");
      setReportSuccess("");

      const base = process.env.REACT_APP_BASE_URL || "/";

      if (!reportPin) {
        setReportError("Click the map to drop a pin for your report.");
        enterPickMode();
        return;
      }

      const heightMeters = convertWaveToMeters(reportForm.waveHeight, waveUnit);
      if (!Number.isFinite(heightMeters) || heightMeters <= 0) {
        setReportError(`Enter a valid wave height in ${waveUnit}.`);
        return;
      }

      const payload = {
        type: "wave_report",
        wave_height_m: heightMeters,
        condition: reportForm.condition, // calm | choppy | rough
        description: reportForm.description?.trim() || null,
        location: reportPin, // { lng, lat }
        unit_entered: waveUnit,
      };

      const res = await fetch(base + "api/reports", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `HTTP ${res.status}`);
      }

      setReportSuccess("Thanks — report submitted!");
      setReportForm({ waveHeight: "", condition: "calm", description: "" });

      // keep the pin or clear it after submit:
      setReportPin(null);
      removeReportMarker();

      setTimeout(() => {
        setReportOpen(false);
        setReportSuccess("");
      }, 700);
    } catch (e) {
      setReportError(String(e.message || e));
    } finally {
      setReportLoading(false);
    }
  }

  const unitToggleStyle = isMobile
  ? {
      position: "absolute",
      scale: 0.8,
      width: "110%",
      top: 12,
      right: 0,
      left: 0,
      zIndex: 50,
      display: "flex",
      gap: 10,
      padding: 10,
      background: "rgba(255,255,255,0.92)",
      borderRadius: 14,
      boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
      userSelect: "none",
      justifyContent: "center", // nice on mobile
    }
  : {
      position: "absolute",
      top: 12,
      right: 25,
      zIndex: 50,
      display: "flex",
      gap: 10,
      padding: 10,
      background: "rgba(255,255,255,0.92)",
      borderRadius: 14,
      boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
      userSelect: "none",
    };

  // ----------------------------
  // Styles (history modal only)
  // ----------------------------
  const styles = {
    backdrop: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.55)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16,
    },
    modal: {
      width: "min(980px, 96vw)",
      maxHeight: "88vh",
      background: "rgba(255,255,255,0.92)",
      borderRadius: 18,
      overflow: "hidden",
      boxShadow: "0 30px 90px rgba(0,0,0,0.45)",
      display: "flex",
      flexDirection: "column",
      border: "1px solid rgba(255,255,255,0.55)",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
    },
    header: {
      padding: "16px 18px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.76))",
    },
    title: { fontWeight: 900, fontSize: 26, letterSpacing: -0.3 },
    subtitle: { fontSize: 14, opacity: 0.7, marginTop: 2 },
    closeBtn: {
      border: "1px solid rgba(0,0,0,0.12)",
      background: "rgba(255,255,255,0.9)",
      borderRadius: 12,
      width: 42,
      height: 42,
      cursor: "pointer",
      fontSize: 22,
      display: "grid",
      placeItems: "center",
      boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
    },
    tabs: {
      padding: "10px 14px",
      borderBottom: "1px solid rgba(0,0,0,0.08)",
      display: "flex",
      gap: 10,
      alignItems: "center",
      background: "rgba(255,255,255,0.78)",
    },
    tabBtn: (active) => ({
      padding: "10px 12px",
      borderRadius: 8,
      border: active ? "1px solid rgba(0,0,0,0.18)" : "1px solid rgba(0,0,0,0.10)",
      background: active
        ? "linear-gradient(180deg, rgba(0,0,0,0.06), rgba(0,0,0,0.02))"
        : "rgba(255,255,255,0.75)",
      fontWeight: 900,
      fontSize: 13,
      cursor: "pointer",
      boxShadow: active ? "0 10px 20px rgba(0,0,0,0.08)" : "none",
    }),
    body: { padding: 14, overflowY: "auto" },

    listWrap: { display: "grid", gap: 10 },
    rowCard: {
      border: "1px solid rgba(0,0,0,0.08)",
      background: "rgba(255,255,255,0.85)",
      borderRadius: 16,
      padding: 12,
      display: "grid",
      gridTemplateColumns: "150px 1fr",
      gap: 12,
      boxShadow: "0 10px 22px rgba(0,0,0,0.06)",
    },
    timeBox: {
      borderRadius: 14,
      padding: 5,
      background:
        "linear-gradient(180deg, rgba(0,0,0,0.04), rgba(0,0,0,0.01))",
      border: "1px solid rgba(0,0,0,0.06)",
      display: "grid",
      alignContent: "center",
      gap: 4,
    },
    timeBig: { fontWeight: 950, fontSize: 18, letterSpacing: -0.3 },
    timeSmall: { fontSize: 12, opacity: 0.65 },
    pills: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" },
    pill: {
      borderRadius: 999,
      padding: "8px 10px",
      border: "1px solid rgba(0,0,0,0.10)",
      background: "rgba(255,255,255,0.75)",
      fontWeight: 850,
      fontSize: 12,
      display: "flex",
      gap: 8,
      alignItems: "center",
    },
    pillMuted: {
      borderRadius: 999,
      padding: "8px 10px",
      border: "1px dashed rgba(0,0,0,0.15)",
      background: "rgba(0,0,0,0.02)",
      fontWeight: 800,
      fontSize: 13,
      opacity: 0.7,
    },
    dirBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 10px",
      borderRadius: 999,
      border: "1px solid rgba(0,0,0,0.10)",
      background: "rgba(255,255,255,0.75)",
      fontWeight: 900,
      fontSize: 13,
      marginLeft: 0,
    },
    dirArrow: (deg) => ({
      display: "inline-block",
      transform: typeof deg === "number" ? `rotate(${deg}deg)` : "none",
      fontSize: 16,
      lineHeight: 1,
      opacity: 0.9,
    }),
  };

  // ----------------------------
  // UI
  // ----------------------------
  return (
    <div style={{ position: "fixed", inset: 0, overflow: "hidden" }}>
      <div
        ref={mapContainerRef}
        style={{ position: "absolute", inset: 0, zIndex: 0, overflow: "hidden" }}
      />

      {/* ✅ pick-mode banner */}
      {reportOpen && reportPicking && (
        <div
          style={{
            position: "absolute",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 120,
            padding: "10px 12px",
            borderRadius: 14,
            background: "rgba(0,0,0,0.75)",
            color: "white",
            fontWeight: 900,
            fontSize: 13,
            boxShadow: "0 14px 40px rgba(0,0,0,0.35)",
            userSelect: "none",
          }}
        >
          Click the map to place your report pin
        </div>
      )}

      {/* ✅ Self Report floating button */}
      <button
        type="button"
        className="report-fab"
        onClick={(e) => {
          e.stopPropagation();
          openReport();
        }}
        title="Report what you’re seeing"
      >
        <span className="dot" />
        <span>Self report</span>
      </button>

    {/* ✅ Unit toggle UI (segmented) */}
    <div
      style={unitToggleStyle}
      onClick={(e) => e.stopPropagation()}
    >
      {/* WIND */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: "#222" }}>
          WIND
        </div>
        <div className="segmented">
          {[
            ["kt", "KT"],
            ["kmh", "KM/H"],
            ["mph", "MPH"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`seg-btn ${windUnit === value ? "is-active" : ""}`}
              onClick={() => setWindUnit(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ width: 1, background: "rgba(0,0,0,0.12)", margin: "4px 2px" }} />

      {/* WAVES (units) */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: "#222" }}>
          WAVES
        </div>
        <div className="segmented">
          {[
            ["m", "M"],
            ["ft", "FT"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`seg-btn ${waveUnit === value ? "is-active" : ""}`}
              onClick={() => setWaveUnit(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ width: 1, background: "rgba(0,0,0,0.12)", margin: "4px 2px" }} />

      {/* WAVE MULTIPLIER (calibration) */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 80 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, color: "#222" }}>
          WAVE CALIBRATION
        </div>

        <div className="segmented">
          {[
            [1, "Original"],
            [2, "2×"],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`seg-btn ${waveMultiplier === value ? "is-active" : ""}`}
              onClick={() => setWaveMultiplier(value)}
              aria-pressed={waveMultiplier === value}
              title={
                value === 1
                  ? "Use the government-reported value as-is"
                  : "Double reported value (if buoy reports calm→crest)"
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>

      {/* ✅ overlay markers */}
      {overlays.map((o) => {
        const pos = positions[o.id];
        if (!pos) return null;

        const isWind = o.type === "wind";
        const text = isWind
          ? formatWindValueFromText(o.wind_knots)
          : formatWaveValue(o.wave_height_m * waveMultiplier);

        const windFrom = parseWindDirectionDegrees(o.wind_knots);
        const dirTo =
          isWind && typeof windFrom === "number"
            ? (windFrom + 180 - 270 + 360) % 360
            : 0;

        return (
          <div
            key={o.id}
            className="map-overlay"
            style={{
              position: "absolute",
              zIndex: 120,             // higher than report icons (80)
              pointerEvents: "none",   // lets map clicks through EXCEPT the bubble
              transform: `translate(${pos.x}px, ${pos.y}px) translate(-50%, -50%)`,
            }}
          >
            <div
              style={{ pointerEvents: "auto", cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveOverlay(o);
                  // if this click is supposed to open history directly, call openHistory(o) here instead
                  // openHistory(o);
                }}   
              >
              {isWind ? (
                <div className="wind-marker">
                  <div className="bubble bubble--wind">
                    <span className="bubble-text">{text}</span>
                  </div>
                  <div className="wind-arrow" style={{ "--dir": `${dirTo}deg` }} />
                </div>
              ) : (
                <div className="bubble bubble--wave">
                  <span className="bubble-text">{text}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* ✅ wave popup */}
{activeOverlay && activeOverlay.type === "wave" && (
  <div
    style={{
      position: "absolute",
      left: positions[activeOverlay.id]?.x,
      top: positions[activeOverlay.id]?.y,
      transform: "translate(-50%, -120%)",
      width: 220,
      background: "white",
      padding: 10,
      borderRadius: 8,
      boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
      zIndex: 300, // ✅ above report card (200) + icons
      fontSize: 12,
      pointerEvents: "auto",
    }}
    onClick={(e) => e.stopPropagation()}
  >
    <strong>Wave Conditions</strong>
    <div>Wind: {activeOverlay.wind_text} kts</div>
    <div>
      Wave height: {formatWaveValue(activeOverlay.wave_height_m * waveMultiplier)}
    </div>
    <div>Wave period: {activeOverlay.wave_period_s} s</div>
    <div>Air temp: {activeOverlay.air_temperature_c} °C</div>
    <div>Water temp: {activeOverlay.water_temperature_c} °C</div>
    <div>
      Pressure: {activeOverlay.pressure_kpa} kPa ({activeOverlay.pressure_tendency})
    </div>

    <button
      className="history-btn"
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        openHistory(activeOverlay);
      }}
      style={{ marginTop: 10, width: "100%" }}
    >
      View history
    </button>
  </div>
)}

{/* ✅ wind popup */}
{activeOverlay && activeOverlay.type === "wind" && (
  <div
    style={{
      position: "absolute",
      left: positions[activeOverlay.id]?.x,
      top: positions[activeOverlay.id]?.y,
      transform: "translate(-50%, -120%)",
      width: 220,
      background: "white",
      padding: 10,
      borderRadius: 8,
      boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
      zIndex: 300,
      fontSize: 12,
      pointerEvents: "auto",
    }}
    onClick={(e) => e.stopPropagation()}
  >
    <strong>Wind Conditions</strong>
    <div>Wind: {activeOverlay.wind_knots} kts</div>
    <div>Conditions: {activeOverlay.conditions ?? "N/A"}</div>
    <div>Visibility: {activeOverlay.visibility_km} km</div>
    <div>Pressure: {activeOverlay.pressure_kpa} kPa</div>
    <div>Air temp: {activeOverlay.air_temperature_c} °C</div>
    <div>RH: {activeOverlay.relative_humidity_pct}%</div>
    <div>Dew point: {activeOverlay.dew_point_c} °C</div>
    <div>Wind chill: {activeOverlay.wind_chill_c} °C</div>

    <button
      className="history-btn"
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        openHistory(activeOverlay);
      }}
      style={{ marginTop: 10, width: "100%" }}
    >
      View history
    </button>
  </div>
)}

      {/* ✅ recent user reports */}
{recentReports.map((r) => {
  const pos = reportPositions[r.id];
  if (!pos) return null;

  const label =
    r.condition === "calm" ? "🟣" : r.condition === "choppy" ? "🟡" : "🔴";

  return (
    <div
      key={`report-${r.id}`}
      style={{
        position: "absolute",
        zIndex: 80,
        transform: `translate(${pos.x}px, ${pos.y}px) translate(-50%, -50%)`,
        pointerEvents: "auto",
      }}
      onClick={(e) => {
        e.stopPropagation();
        setActiveReport(r);
      }}
      title="User report"
    >
      <div
        style={{
          boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
          display: "grid",
          placeItems: "center",
          fontSize: 12,
          cursor: "pointer",
        }}
      >
        {label}
      </div>
    </div>
  );
})}

{activeReport && (
  <div
    style={{
      position: "absolute",
      left: 16,
      bottom: 16,
      zIndex: 200,
      width: 360,
      maxWidth: "calc(100vw - 32px)",
      borderRadius: 18,
      overflow: "hidden",
      border: "1px solid rgba(0,0,0,0.10)",
      background: "rgba(255,255,255,0.92)",
      boxShadow: "0 26px 70px rgba(0,0,0,0.28)",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
    }}
    onClick={(e) => e.stopPropagation()}
  >
    {(() => {
      const condition = (activeReport.condition || "").toLowerCase();
      const meta =
        condition === "calm"
          ? { label: "Calm", dot: "🟣" }
          : condition === "choppy"
          ? { label: "Choppy", dot: "🟡" }
          : { label: "Rough", dot: "🔴" };

      const waveVal = convertWaveMeters(activeReport.wave_height_m, waveUnit);
      const waveText =
        Number.isFinite(waveVal)
          ? `${waveVal.toFixed(waveUnit === "m" ? 2 : 1)} ${waveUnit}`
          : "—";

      const when = new Date(activeReport.created_at).toLocaleString("en-CA", {
        timeZone: "America/Vancouver",
        hour12: false,
      });

      return (
        <>
          {/* top strip */}
          <div
            style={{
              padding: "12px 14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.03), rgba(0,0,0,0.00))",
              borderBottom: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <div style={{ fontWeight: 950, fontSize: 16, letterSpacing: -0.2 }}>
                User wave report
              </div>
              <div style={{ fontSize: 12, opacity: 0.72 }}>{when}</div>
            </div>

            <button
              type="button"
              onClick={() => setActiveReport(null)}
              aria-label="Close"
              style={{
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(255,255,255,0.95)",
                cursor: "pointer",
                fontSize: 18,
                fontWeight: 900,
                display: "grid",
                placeItems: "center",
                boxShadow: "0 10px 22px rgba(0,0,0,0.10)",
              }}
            >
              ✕
            </button>
          </div>

          {/* body */}
          <div style={{ padding: 14, display: "grid", gap: 12 }}>
            {/* big stat row */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 12, opacity: 0.65, fontWeight: 800 }}>
                  Wave height
                </div>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 950,
                    letterSpacing: -0.6,
                    lineHeight: 1.05,
                  }}
                >
                  {waveText}
                </div>
              </div>

              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 12px",
                  borderRadius: 999,
                  border: "1px solid rgba(0,0,0,0.10)",
                  background: "rgba(255,255,255,0.75)",
                  fontWeight: 900,
                  fontSize: 13,
                  textTransform: "capitalize",
                  boxShadow: "0 10px 22px rgba(0,0,0,0.06)",
                }}
              >
                <span style={{ fontSize: 14, lineHeight: 1 }}>{meta.dot}</span>
                <span>{meta.label}</span>
              </div>
            </div>

            {/* optional description */}
            {activeReport.description && (
              <div
                style={{
                  borderRadius: 14,
                  padding: 12,
                  border: "1px solid rgba(0,0,0,0.08)",
                  background: "rgba(0,0,0,0.02)",
                  fontSize: 14,
                  lineHeight: 1.35,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 900, opacity: 0.65, marginBottom: 6 }}>
                  NOTE
                </div>
                <div>“{activeReport.description}”</div>
              </div>
            )}

            {/* location row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                paddingTop: 4,
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  borderRadius: 999,
                  border: "1px solid rgba(0,0,0,0.10)",
                  background: "rgba(255,255,255,0.75)",
                  fontSize: 12,
                  fontWeight: 850,
                  opacity: 0.9,
                }}
                title="Location"
              >
                <span style={{ opacity: 0.8 }}>📍</span>
                <span>
                  {activeReport.lat.toFixed(5)}, {activeReport.lng.toFixed(5)}
                </span>
              </div>

              <button
                type="button"
                onClick={() => {
                  const map = mapRef.current;
                  if (!map) return;
                  map.flyTo({
                    center: [activeReport.lng, activeReport.lat],
                    zoom: Math.max(map.getZoom(), 11),
                    speed: 1.2,
                  });
                }}
                style={{
                  borderRadius: 12,
                  border: "1px solid rgba(0,0,0,0.10)",
                  background: "rgba(255,255,255,0.85)",
                  padding: "10px 12px",
                  cursor: "pointer",
                  fontWeight: 900,
                  fontSize: 12,
                  boxShadow: "0 10px 22px rgba(0,0,0,0.06)",
                }}
              >
                Jump to pin
              </button>
            </div>
          </div>
        </>
      );
    })()}
  </div>
)}

      {/* ✅ SELF REPORT MODAL */}
      {reportOpen && (
        <div className="report-modal-backdrop" role="presentation" onClick={closeReport}>
          <div className="report-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="report-modal-header">
              <div>
                <div className="report-modal-title">Self report — waves</div>
                <div className="report-help">
                  {reportPin
                    ? "You can drag the pin on the map to adjust."
                    : "Click “Pick on map” then click the map to place your report pin."}
                </div>
              </div>

              <button
                type="button"
                onClick={closeReport}
                style={{
                  border: "1px solid rgba(0,0,0,0.12)",
                  background: "rgba(255,255,255,0.9)",
                  borderRadius: 12,
                  width: 42,
                  height: 42,
                  cursor: "pointer",
                  fontSize: 22,
                  display: "grid",
                  placeItems: "center",
                  boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
                }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="report-modal-body">
              {reportError && <div className="report-msg err">Error: {reportError}</div>}
              {reportSuccess && <div className="report-msg ok">{reportSuccess}</div>}

              <div className="report-row">
                <div className="report-label">Wave height ({waveUnit})</div>
                <input
                  className="report-input"
                  inputMode="decimal"
                  placeholder={waveUnit === "m" ? "e.g., 0.8" : "e.g., 3"}
                  value={reportForm.waveHeight}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^\d.]/g, "");
                    setReportForm((p) => ({ ...p, waveHeight: v }));
                  }}
                />
              </div>

              <div className="report-row">
                <div className="report-label">Conditions</div>
                <select
                  className="report-select"
                  value={reportForm.condition}
                  onChange={(e) => setReportForm((p) => ({ ...p, condition: e.target.value }))}
                >
                  <option value="calm">Calm</option>
                  <option value="choppy">Choppy</option>
                  <option value="rough">Rough</option>
                </select>
              </div>

              <div className="report-row">
                <div className="report-label">Description (optional)</div>
                <textarea
                  className="report-textarea"
                  placeholder="Anything helpful… whitecaps, swell direction, boat traffic, etc."
                  value={reportForm.description}
                  onChange={(e) => setReportForm((p) => ({ ...p, description: e.target.value }))}
                />
              </div>

              <div className="report-row">
                <div className="report-label">Pin location</div>
                <div className="report-help">
                  {reportPin
                    ? `Pinned: ${reportPin.lat.toFixed(5)}, ${reportPin.lng.toFixed(5)}`
                    : "No pin yet."}
                </div>
              </div>
            </div>

            <div className="report-actions">
              <button
                type="button"
                className="report-btn secondary"
                onClick={() => {
                  setReportError("");
                  setReportSuccess("");

                  setReportOpen(false);     // hide modal so map is clickable
                  enterPickMode();          // turn on crosshair + banner
                }}
                disabled={reportLoading}
              >
                Pick / move pin
              </button>

              <button
                type="button"
                className="report-btn primary"
                onClick={submitReport}
                disabled={reportLoading}
              >
                {reportLoading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ HISTORY MODAL */}
      {historyOpen && (
        <div onClick={closeHistory} role="presentation" style={styles.backdrop}>
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            style={styles.modal}
          >
            <div style={styles.header}>
              <div>
                <div style={styles.title}>Last 12 hours — {historyMetricLabel}</div>
                <div style={styles.subtitle}>{historyTarget?.station_id || historyTarget?.id}</div>
              </div>

              <button onClick={closeHistory} type="button" style={styles.closeBtn} aria-label="Close">
                ✕
              </button>
            </div>

            <div style={styles.tabs}>
              {[
                ["graph", "Graph"],
                ["list", "List"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setHistoryTab(key)}
                  style={styles.tabBtn(historyTab === key)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div style={styles.body}>
              {historyLoading && <div style={{ opacity: 0.7, fontWeight: 700 }}>Loading…</div>}
              {historyError && (
                <div style={{ color: "crimson", fontWeight: 800 }}>Error: {historyError}</div>
              )}

              {!historyLoading && !historyError && chartData.length === 0 && (
                <div style={{ opacity: 0.7, fontWeight: 700 }}>No history found.</div>
              )}

              {!historyLoading && !historyError && chartData.length > 0 && (
                <>
                  {historyTab === "graph" && (
                    <div style={{ width: "100%", height: 420 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 12, right: 18, bottom: 10, left: 0 }}>
                          <CartesianGrid stroke="rgba(0,0,0,0.08)" vertical={false} />

                          <XAxis
                            dataKey="time"
                            tick={{ fontSize: 12 }}
                            axisLine={false}
                            tickLine={false}
                            interval="preserveStartEnd"
                          />

                          <YAxis
                            tick={{ fontSize: 12 }}
                            axisLine={false}
                            tickLine={false}
                            width={40}
                            domain={["auto", "auto"]}
                          />

                          <Tooltip
                            labelFormatter={tooltipLabel}
                            formatter={tooltipFormatter}
                            contentStyle={{
                              borderRadius: 14,
                              border: "1px solid rgba(0,0,0,0.10)",
                              boxShadow: "0 18px 40px rgba(0,0,0,0.18)",
                            }}
                          />

                          <Area
                            type="monotone"
                            dataKey={historyMetric}
                            stroke={historyMetric === "wave" ? "#7c3aed" : "#2563eb"}
                            strokeWidth={3}
                            fill="rgba(37,99,235,0.08)"
                            dot={false}
                            connectNulls
                            isAnimationActive={false}
                          />

                          {historyMetric === "wind" && (
                            <Scatter
                              data={dirMarkers}
                              dataKey="wind"
                              shape={(props) => {
                                const { cx, cy, payload } = props;
                                if (!cx || !cy) return null;
                                const deg = payload?.dirDeg;
                                return (
                                  <g transform={`translate(${cx}, ${cy})`}>
                                    <text
                                      x={0}
                                      y={4}
                                      textAnchor="middle"
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 900,
                                        fill: "rgba(0,0,0,0.45)",
                                        transform: typeof deg === "number" ? `rotate(${deg})` : undefined,
                                      }}
                                    >
                                      →
                                    </text>
                                  </g>
                                );
                              }}
                            />
                          )}
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {historyTab === "list" && (
                    <div style={styles.listWrap}>
                      {chartData
                        .slice()
                        .reverse() // newest first (optional)
                        .map((d) => {
                          const t = d.raw?.observed_at || d.raw?.ts || d.raw?.timestamp;
                          const value =
                            historyMetric === "wave"
                              ? d.wave
                              : d.wind;

                          return (
                            <div key={d.tsMs} style={styles.rowCard}>
                              <div style={styles.timeBox}>
                                <div style={styles.timeBig}>{d.time}</div>
                                <div style={styles.timeSmall}>{t ? pacificDateTime(t) : "—"}</div>
                              </div>

                              <div style={{ display: "grid", gap: 10 }}>
                                <div style={styles.pills}>
                                  <div style={styles.pill}>
                                    {historyMetric === "wave" ? "🌊" : "💨"}{" "}
                                    <span>
                                      {value == null
                                        ? "—"
                                        : historyMetric === "wave"
                                        ? `${Number(value).toFixed(waveUnit === "m" ? 2 : 1)} ${waveUnit}`
                                        : `${Math.round(Number(value))} ${windUnit}`}
                                    </span>
                                  </div>

                                  {historyMetric === "wind" && (
                                    <div style={styles.dirBadge}>
                                      <span style={styles.dirArrow(d.dirDeg)}>{arrowFromDegrees(d.dirDeg)}</span>
                                      <span>{d.dir || "—"}</span>
                                    </div>
                                  )}
                                </div>

                                {/* Optional: show raw text too */}
                                {historyMetric === "wind" && (
                                  <div style={styles.pillMuted}>
                                    Raw: {d.raw?.wind_knots ?? "—"}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
}
